import { useEffect, useRef } from "react";
import {
  ApolloError,
  FetchPolicy,
  useQuery,
  useSubscription,
} from "@apollo/client";
import { useSetRecoilState } from "recoil";
import { OC_GET_PROPERTY, OC_PROPERTY_SUBSCRIPTION } from "oc/graphql";
import { ocNotificationState } from "oc/states/notification-state";

interface UseOcPropertyParams {
  resource?: string;
  key?: string;
  onCompleted?: (result: { key: string; value: any }) => void;
  onError?: (result: { key: string; value: ApolloError }) => void;
  fetchPolicy?: FetchPolicy;
  pollInterval?: number;
  skip?: boolean;
  subscription?: boolean;
}

interface PropertyQueryData {
  property: {
    key: string;
    value: any;
  };
}

interface PropertyQueryVars {
  resource: string;
  key: string;
}

export const useOcProperty = ({
  resource,
  key,
  onCompleted,
  fetchPolicy,
  pollInterval = 0,
  skip,
  subscription = true,
  onError,
}: UseOcPropertyParams) => {
  const skipValue = skip || !resource || !key;
  const onCompletedCalledRef = useRef(false);
  const onErrorCalledRef = useRef(false);

  const { data, loading, error, refetch } = useQuery<
    PropertyQueryData,
    PropertyQueryVars
  >(OC_GET_PROPERTY, {
    skip: skipValue,
    fetchPolicy: fetchPolicy || "cache-and-network",
    pollInterval,
    variables: {
      resource: resource!,
      key: key!,
    },
  });

  // Handle onCompleted callback via useEffect
  useEffect(() => {
    if (data && !loading && onCompleted && key && !onCompletedCalledRef.current) {
      onCompletedCalledRef.current = true;
      onCompleted({ key, value: data?.property?.value });
    }
  }, [data, loading, onCompleted, key]);

  // Reset the called ref when key or resource changes
  useEffect(() => {
    onCompletedCalledRef.current = false;
    onErrorCalledRef.current = false;
  }, [key, resource]);

  // Handle onError callback via useEffect
  useEffect(() => {
    if (error && onError && key && !onErrorCalledRef.current) {
      onErrorCalledRef.current = true;
      onError({ key, value: error });
    }
  }, [error, onError, key]);

  useSubscription(OC_PROPERTY_SUBSCRIPTION, {
    skip: !subscription || !resource || !key,
    variables: {
      resource: resource!,
      key: key!,
    },
    onData: () => {
      onCompletedCalledRef.current = false; // Allow callback again on subscription update
      refetch();
    },
  });

  const setOpenMessage = useSetRecoilState(ocNotificationState);

  useEffect(() => {
    if (error) {
      setOpenMessage({
        type: "error",
        message: "NETWORK_ERROR",
      });
    }
  }, [error, setOpenMessage]);

  return {
    data: data?.property?.value,
    loading,
    error,
    refetch,
    onCompleted,
    onError,
  };
};

export default useOcProperty;
